import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { animate, stagger } from "animejs";
import { shouldReduceMotion } from "../../utils/motion";
import {
  FaRobot, FaBrain, FaDatabase, FaChartLine, FaFileAlt,
  FaBuilding, FaSearch, FaCode, FaTimes, FaArrowRight
} from "react-icons/fa";
import "./Projects.scss";

// Real projects from WorkProfileWiki
const projects = [
  {
    name: 'Brand Automations Suite',
    category: 'Marketing AI',
    icon: <FaBrain />,
    preview: 'Multi-agent podcast lifecycle engine and LinkedIn content system using Agents-as-Tools pattern with PGVector for 3 leadership handbooks.',
    description: `A high-volume AI content distribution engine for a personal brand growth platform. Spans podcast post-production (OneDrive transcripts → AI insights → social posts), automated reel distribution across LinkedIn and Facebook, and a sophisticated multi-agent LinkedIn chatbot.\n\nThe chatbot uses an Orchestrator-as-Router pattern coordinating 3 specialist RAG agents (SFD, LFD, RDB) each querying dedicated leadership handbooks via PGVector semantic retrieval. Up to 25 research iterations per post with British English output polish.`,
    tech: ['n8n', 'PGVector', 'OpenAI', 'LinkedIn API', 'Facebook API', 'OneDrive', 'Trello'],
  },
  {
    name: 'DataSnag Ecosystem',
    category: 'Lead Generation',
    icon: <FaChartLine />,
    preview: 'Scalable B2B data extraction platform. FastAPI + Next.js frontend over Selenium/Lambda scrapers with PostgreSQL + Redis pipeline.',
    description: `A vertically integrated lead generation platform. The backend orchestrates distributed AWS Lambda scraping workers (Selenium-based) with full deduplication, rate-limiting, and proxy rotation.\n\nFrontend built in Next.js provides real-time job status, bulk export, and filter UX. PostgreSQL stores enriched lead records; Redis queues background jobs. Designed for 10,000+ record runs without manual intervention.`,
    tech: ['FastAPI', 'Next.js', 'Selenium', 'AWS Lambda', 'PostgreSQL', 'Redis', 'Docker'],
  },
  {
    name: 'Restaurant OS Ecosystem',
    category: 'Hospitality Tech',
    icon: <FaBuilding />,
    preview: 'Full-stack restaurant management OS. Vue 3 + FastAPI with event-freezing architecture, Alembic-managed PostgreSQL, and real-time floor management.',
    description: `A production-grade restaurant operating system built from scratch. Core features include table management with event-freezing (prevents race conditions on concurrent order edits), kitchen display integration, inventory tracking, and shift reporting.\n\nBuilt on FastAPI with Alembic for zero-downtime database migrations. Vue 3 SPA frontend with real-time WebSocket updates for order status. Deployed dockerized on VPS.`,
    tech: ['FastAPI', 'Vue 3', 'PostgreSQL', 'Alembic', 'WebSockets', 'Docker'],
  },
  {
    name: 'Vectorless RAG Engine',
    category: 'AI Research',
    icon: <FaBrain />,
    preview: 'Novel hierarchical semantic retrieval system without a vector database. Uses Mistral OCR + PageIndex tree structure for document intelligence.',
    description: `An experimental but production-deployed RAG architecture that eliminates the need for a vector database entirely. Documents are processed via Mistral OCR, then indexed into a hierarchical PageIndex tree enabling O(log n) semantic lookups.\n\nThe engine uses a retrieve-as-tool pattern where the LLM calls index traversal functions rather than embedding search. Achieves comparable recall to PGVector setups with zero infrastructure overhead.`,
    tech: ['Python', 'n8n', 'Mistral OCR', 'PageIndex', 'FastAPI', 'Semantic Tree'],
  },
  {
    name: 'Islamic RAG System',
    category: 'AI / Knowledge',
    icon: <FaDatabase />,
    preview: 'Production RAG system over Islamic texts. FastAPI + pgvector with Gemini embeddings, semantic chunking, and citation-aware answer generation.',
    description: `A domain-specific RAG pipeline for Islamic scholarly texts. Uses Google Gemini for high-quality multilingual embeddings stored in PostgreSQL via pgvector extension.\n\nCustom chunking respects verse boundaries and citation structure. The retrieval layer surfaces relevant Hadith/Quran passages alongside scholarly commentary. Answer generation enforces citation formatting and uncertainty hedging for religious accuracy standards.`,
    tech: ['FastAPI', 'PostgreSQL', 'pgvector', 'Gemini Embeddings', 'Python', 'Docker'],
  },
  {
    name: 'DeathCertificate OCR System',
    category: 'Legal / FinTech',
    icon: <FaFileAlt />,
    preview: 'Dual-agent consensus OCR pipeline for legal estate documents. Mistral AI cross-validates extractions before populating HubSpot and generating legal templates.',
    description: `An agentic OCR system handling death certificates and estate settlement documents. Two independent Mistral AI agents extract structured data from document images; a consensus layer validates agreement before committing results.\n\nExtracted fields auto-populate HubSpot CRM records and trigger Docx legal template generation. Includes human-in-the-loop review flagging for low-confidence extractions.`,
    tech: ['n8n', 'Mistral AI', 'HubSpot', 'Python', 'Docx Templates', 'Dual-Agent Consensus'],
  },
  {
    name: 'Auto Scrappers Platform',
    category: 'Automotive / Lead Gen',
    icon: <FaSearch />,
    preview: 'Serverless vehicle inventory intelligence system. AWS SAM + Playwright Lambda workers scraping dealer networks into PostgreSQL with anomaly detection.',
    description: `A distributed vehicle data extraction system for automotive dealership intelligence. AWS SAM manages the Lambda function lifecycle; Playwright runs headless browser scrapers within Lambda containers for JS-heavy dealer portals.\n\nInventory records are normalized, deduplicated, and stored in PostgreSQL with change-detection to flag price drops and new listings. Automated alerts fire when target vehicles match watchlist criteria.`,
    tech: ['Python', 'AWS SAM', 'AWS Lambda', 'Playwright', 'PostgreSQL', 'SES Alerts'],
  },
  {
    name: 'Properties Automations',
    category: 'Real Estate CRM',
    icon: <FaBuilding />,
    preview: 'End-to-end real estate CRM automation. Aircall + HubSpot integration with ElevenLabs voice AI, lead scoring, and automated follow-up sequences.',
    description: `A complete CRM automation stack for a real estate operation. Inbound Aircall calls are transcribed and summarized by AI before creating/updating HubSpot contact records. ElevenLabs generates personalized voice messages for follow-up outreach.\n\nLead scoring workflow runs daily, prioritizing contacts by engagement signals. Automated email and WhatsApp sequences trigger based on pipeline stage transitions.`,
    tech: ['n8n', 'HubSpot', 'Aircall', 'ElevenLabs', 'WhatsApp API', 'Google Workspace'],
  },
  {
    name: 'PeerPulse',
    category: 'HR Tech / SaaS',
    icon: <FaRobot />,
    preview: 'Multi-tier performance review SaaS platform. Next.js 14 + Express + MySQL with Stripe billing, TypeORM, and 360-degree review workflows.',
    description: `A full-featured HR SaaS platform for 360-degree performance reviews. Supports peer, manager, and self-review cycles with configurable question templates and scoring rubrics.\n\nBuilt on Next.js 14 App Router with an Express API layer. TypeORM manages MySQL schema with Redis for session caching. Stripe integration handles subscription billing with per-seat pricing. Deployed on AWS with autoscaling.`,
    tech: ['Next.js 14', 'Express', 'MySQL', 'Redis', 'TypeORM', 'Stripe', 'AWS'],
  },
  {
    name: 'Restaurant POS',
    category: 'Frappe / ERPNext',
    icon: <FaCode />,
    preview: 'Custom Frappe/ERPNext app for restaurant point-of-sale operations with table and counter order models, kitchen display sync, and a clean UI sandboxed from the ERPNext Desk.',
    description: `A bespoke Frappe v16 / ERPNext v16 app built as a clean, role-specific POS operating system. Implements Table Order and Counter Order models with real-time kitchen display integration.\n\nThe app provides a dedicated sidebar interface for restaurant users — no ERPNext Desk, no unused modules visible. Includes custom DocTypes, permissions, and API endpoints for seamless order lifecycle management. Uses OpenRouter AI for smart order suggestions.\n\nBuilt from scratch as a stand-alone Frappe app with its own frontend Frappe UI / Vue layer, designed for single-branch deployment.`,
    tech: ['Frappe v16', 'ERPNext v16', 'Vue', 'OpenRouter', 'Docker', 'Custom App'],
  },
  {
    name: 'ERPNext Management Suite',
    category: 'Frappe / ERPNext',
    icon: <FaCode />,
    preview: 'Multi-module ERPNext management platform with 7 custom modules, Dockerized Coolify deployment, NAS sync, and full CI/CD pipeline with E2E testing.',
    description: `A comprehensive ERPNext/Frappe management suite built across 7 custom modules: core, growth, creative, shop, loot, operations, and integrations.\n\nArchitected with Docker Compose on Coolify with GitHub Actions CI/CD, automated staging/production deployments, and a NAS sync worker for passive archiving. Includes Shopify HMAC webhook handling, custom DocType controllers, and a full E2E testing pipeline with Playwright.\n\nAll 6 domains routed through a single Frappe site with ERPNext owning the entire business system — no parallel CRM or custom PostgreSQL source of truth.`,
    tech: ['Frappe v16', 'ERPNext v16', 'Docker', 'Coolify', 'GitHub Actions', 'Playwright', 'NAS Sync'],
  },
];

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25 } },
  exit:    { opacity: 0, transition: { duration: 0.2 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.88, y: 24 },
  visible: {
    opacity: 1, scale: 1, y: 0,
    transition: { type: 'spring', stiffness: 280, damping: 28 }
  },
  exit: {
    opacity: 0, scale: 0.92, y: 12,
    transition: { duration: 0.2, ease: 'easeIn' }
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { type: 'spring', stiffness: 200, damping: 22, delay: i * 0.07 }
  })
};

const Projects = () => {
  const [selected, setSelected] = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const rootRef = useRef(null);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!rootRef.current || shouldReduceMotion()) return undefined;

    const cards = rootRef.current.querySelectorAll(".projects__card");
    const animation = animate(cards, {
      opacity: [0, 1],
      translateY: [24, 0],
      scale: [0.96, 1],
      delay: stagger(55),
      duration: 620,
      ease: "outExpo"
    });

    return () => animation.revert();
  }, [activeFilter]);

  useEffect(() => {
    if (!selected) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelected(null);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selected]);

  const openProject = (project) => {
    setSelected(project);
  };

  const handleProjectKeyDown = (project) => (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openProject(project);
  };

  const filtered = activeFilter === "All"
    ? projects
    : projects.filter(p => p.category === activeFilter);

  return (
    <section id="projects" className="projects" ref={rootRef}>
      <div className="projects__content">
        <motion.h2
          className="projects__heading"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 200, damping: 22 }}
        >
          Featured Work
        </motion.h2>

        <motion.p
          className="projects__subtitle"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 180 }}
        >
          Production systems across 15+ client ecosystems and 2 custom Frappe/ERPNext apps
        </motion.p>

        {/* Filter tabs */}
        <motion.div
          className="projects__filters"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
        >
          {["All", "Frappe / ERPNext", "AI / Knowledge", "Lead Generation", "Marketing AI", "Legal / FinTech"].map(cat => (
            <motion.button
              key={cat}
              type="button"
              className={`filter-btn ${activeFilter === cat ? 'active' : ''}`}
              onClick={() => setActiveFilter(cat)}
              aria-pressed={activeFilter === cat}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {cat}
            </motion.button>
          ))}
        </motion.div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            className="projects__grid"
            aria-live="polite"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {filtered.map((project, i) => (
              <motion.article
                key={project.name}
                className="projects__card"
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                onClick={() => openProject(project)}
                onKeyDown={handleProjectKeyDown(project)}
                role="button"
                tabIndex={0}
                aria-label={`Open details for ${project.name}`}
                whileHover={{ y: -10 }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              >
                <div className="projects__card-header">
                  <div className="card-icon">{project.icon}</div>
                  <span className="card-category">{project.category}</span>
                </div>

                <h3>{project.name}</h3>
                <p className="projects__card-preview">{project.preview}</p>

                <div className="projects__card-footer">
                  {project.tech.slice(0, 3).map(t => (
                    <span key={t} className="tech-pill">{t}</span>
                  ))}
                  <span className="read-more-hint">
                    Details <FaArrowRight size={10} />
                  </span>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              className="projects__overlay"
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={() => setSelected(null)}
              aria-hidden="true"
            />
            <motion.div
              ref={modalRef}
              className="projects__modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="project-modal-title"
            >
              <button
                ref={closeButtonRef}
                type="button"
                className="close-btn"
                onClick={() => setSelected(null)}
                aria-label="Close project details"
              >
                <FaTimes />
              </button>

              <div className="projects__modal-scroll">
                <div className="projects__modal-header">
                  <div className="modal-icon">{selected.icon}</div>
                  <div className="modal-meta">
                    <div className="modal-category">{selected.category}</div>
                    <h3 id="project-modal-title">{selected.name}</h3>
                  </div>
                </div>

                <div className="projects__modal-body">
                  {selected.description.split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>

                <div className="projects__modal-tags">
                  {selected.tech.map(t => (
                    <span key={t} className="tech-pill">{t}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
